import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { useAuth } from '../contexts/AuthContext';
import TemplateSelector from '../components/contracts/TemplateSelector';
import ContractDetailsForm from '../components/contracts/ContractDetailsForm';
import ContractDescriptionForm from '../components/contracts/ContractDescriptionForm';
import ContractPreview from '../components/contracts/ContractPreview';
import { getContractTemplates, generateContract, generateContractFromDescription, saveGeneratedContract, convertContractToDocument } from '../utils/contractService';
import './ContractGeneratorPage.css';

const ContractGeneratorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [generationMethod, setGenerationMethod] = useState(null); // 'template' or 'description'
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contractDetails, setContractDetails] = useState({});
  const [generatedContract, setGeneratedContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await getContractTemplates();
        setTemplates(data);
      } catch (err) {
        setError('Failed to load contract templates');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);
  
  // Handle generation method selection
  const handleMethodSelect = (method) => {
    setGenerationMethod(method);
    setStep(2);
  };
  
  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(3);
    
    // Initialize contract details with empty values based on template schema
    const initialDetails = {};
    const fields = template.form_schema.fields || [];
    
    fields.forEach(field => {
      initialDetails[field.id] = field.default || '';
    });
    
    setContractDetails(initialDetails);
  };
  
  // Handle form field changes
  const handleFieldChange = (fieldId, value) => {
    setContractDetails(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };
  
  // Handle template-based form submission
  const handleDetailsSubmit = async (e) => {
    if (e) e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Generate contract using OpenAI with template approach
      const contractText = await generateContract({
        templateType: selectedTemplate.template_type,
        contractDetails
      });
      
      // Save the generated contract
      const savedContract = await saveGeneratedContract({
        user_id: user.id,
        template_id: selectedTemplate.id,
        contract_data: contractDetails,
        contract_content: contractText,
        status: 'draft'
      });
      
      setGeneratedContract(savedContract);
      setStep(4);
    } catch (err) {
      setError('Failed to generate contract: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle description-based form submission
  const handleDescriptionSubmit = async (description) => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate contract using OpenAI with description approach
      const contractText = await generateContractFromDescription(description);
      
      // Save the generated contract without a template
      const savedContract = await saveGeneratedContract({
        user_id: user.id,
        contract_data: { description },
        contract_content: contractText,
        status: 'draft'
      });
      
      setGeneratedContract(savedContract);
      setStep(4);
    } catch (err) {
      setError('Failed to generate contract: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle contract editing
  const handleContractEdit = (updatedContent) => {
    setGeneratedContract(prev => ({
      ...prev,
      contract_content: updatedContent
    }));
  };
  
  // Handle saving as template
  const handleSaveTemplate = async (templateName, isPublic = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Logic to save as a custom template
      // This would be implemented in a real application
      
      setSuccess('Template saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save template: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle finalizing the contract
  const handleFinalize = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Create a document name based on generation method
      let documentName;
      if (selectedTemplate) {
        // Template-based approach
        documentName = `${selectedTemplate.name} - ${new Date().toLocaleDateString()}`;
      } else {
        // Description-based approach
        const contractType = generatedContract.contract_data.description.split(' ').slice(0, 3).join(' ');
        documentName = `${contractType}... - ${new Date().toLocaleDateString()}`;
      }
      
      // Convert to document for e-signature flow
      const document = await convertContractToDocument(
        generatedContract.id,
        documentName
      );
      
      // Navigate to editor to add signature fields
      navigate(`/editor/${document.id}`);
    } catch (err) {
      setError('Failed to finalize contract: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle submitting a review
  const handleSubmitReview = async (rating, feedback) => {
    try {
      setLoading(true);
      setError(null);
      
      // Logic to submit review
      // This would be implemented in a real application
      
      setSuccess('Thank you for your feedback!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to submit review: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="contract-generator-page">
      <div className="container">
        <h1 className="page-title">Contract Generator</h1>
        
        {error && (
          <Alert 
            type="error" 
            title="Error" 
            message={error} 
            dismissible={true}
            onDismiss={() => setError(null)}
            icon={null}
            action={null}
          />
        )}
        
        {success && (
          <Alert 
            type="success" 
            title="Success" 
            message={success} 
            dismissible={true}
            onDismiss={() => setSuccess(null)}
            icon={null}
            action={null}
          />
        )}
        
        <div className="progress-indicator">
          {/* Simplified progress indicator with consistent structure */}
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-text">Choose Method</span>
          </div>
          <div className="progress-connector"></div>
          
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-text">
              {generationMethod === 'template' ? 'Select Template' : 
               generationMethod === 'description' ? 'Describe Contract' : 'Create Contract'}
            </span>
          </div>
          <div className="progress-connector"></div>
          
          <div className={`progress-step ${
            (generationMethod === 'template' && step >= 3) || 
            (generationMethod === 'description' && step >= 4) || 
            step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-text">Review & Finalize</span>
          </div>
        </div>
        
        <Card 
          title=""
          subtitle=""
          footer=""
          className="generator-card"
        >
          {step === 1 && (
            <div className="method-selector">
              <h2>How would you like to create your contract?</h2>
              <p>Choose the method that works best for you.</p>
              
              <div className="method-options">
                <div 
                  className="method-option"
                  onClick={() => handleMethodSelect('template')}
                >
                  <h3>Use a Template</h3>
                  <p>Select from our pre-made templates and fill in the details.</p>
                  <button className="btn btn-primary">Choose Template</button>
                </div>
                
                <div 
                  className="method-option"
                  onClick={() => handleMethodSelect('description')}
                >
                  <h3>Describe What You Need</h3>
                  <p>Tell us what kind of contract you need, and we'll generate it for you.</p>
                  <button className="btn btn-primary">Describe Contract</button>
                </div>
              </div>
            </div>
          )}
          
          {step === 2 && generationMethod === 'template' && (
            <TemplateSelector 
              templates={templates} 
              onSelect={handleTemplateSelect} 
              onBack={() => setStep(1)}
              loading={loading}
            />
          )}
          
          {step === 2 && generationMethod === 'description' && (
            <ContractDescriptionForm
              onSubmit={handleDescriptionSubmit}
              onBack={() => setStep(1)}
              loading={loading}
            />
          )}
          
          {step === 3 && generationMethod === 'template' && selectedTemplate && (
            <ContractDetailsForm 
              template={selectedTemplate}
              details={contractDetails}
              onChange={handleFieldChange}
              onSubmit={handleDetailsSubmit}
              onBack={() => setStep(2)}
              loading={loading}
            />
          )}
          
          {step === 4 && generatedContract && (
            <ContractPreview
              contract={generatedContract}
              template={selectedTemplate}
              onEdit={handleContractEdit}
              onSaveTemplate={handleSaveTemplate}
              onFinalize={handleFinalize}
              onBack={() => generationMethod === 'template' ? setStep(3) : setStep(2)}
              onSubmitReview={handleSubmitReview}
              loading={loading}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default ContractGeneratorPage;
